import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  LaunchPage,
  MaterialUiTable,
  sortByAlphabeticalOrder,
  Badge,
} from "@qivia/ui";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  DriverElectrification,
  DriverElectrificationDisplayed,
  ElectroCompatibleDriver,
} from "./electrificationApi";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import {
  selectDriversList,
  selectDriversListStatus,
} from "../drivers/driversSlice";
import "../../../i18n/i18n";

const fakeData: ElectroCompatibleDriver[] = [
  {
    uuid: "c6f9317b-73bd-49cb-8a44-9116816c7d2c",
    prioritizationScore: 2,
    electroCompatibilityScore: 76,
    drivingScore: 82,
    homeCharging: "COMPATIBLE",
  },
  {
    uuid: "692a764c-af21-4811-baab-392c0ab490c4",
    prioritizationScore: 1,
    electroCompatibilityScore: 42,
    drivingScore: 72,
    homeCharging: "COMPATIBLE",
  },
  {
    uuid: "fe2aa09b-2d79-48c7-b573-0608a9d2c6d2",
    prioritizationScore: 3,
    electroCompatibilityScore: 88,
    drivingScore: 62,
    homeCharging: "INCOMPATIBLE",
  },
  {
    uuid: "ffccb006-09e1-438a-b072-6f2c4fa2cc46",
    prioritizationScore: 4,
    electroCompatibilityScore: 75,
    drivingScore: 62,
    homeCharging: "ALREADY_EQUIPPED",
  },
];

export const DriversTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const electroCompatibleDriversListInit: ElectroCompatibleDriver[] = fakeData;
  const driversListInit = useAppSelector(selectDriversList);
  const driversListStatus = useAppSelector(selectDriversListStatus);

  const [listDisplayed, setListDisplayed] = useState<
    DriverElectrificationDisplayed[] | null
  >(null);

  const pageName = "elecDrivers";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      name: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const driversElectrificationList: DriverElectrification[] = useMemo(() => {
    const driversElectrification: DriverElectrification[] = driversListInit.map(
      (driver) => {
        const driverElectrification = electroCompatibleDriversListInit.find(
          (d) => d.uuid === driver.uuid,
        );
        return {
          uuid: driver.uuid,
          name: driver.name,
          prioritizationScore:
            driverElectrification?.prioritizationScore ?? null,
          electroCompatibilityScore:
            driverElectrification?.electroCompatibilityScore ?? null,
          drivingScore: driverElectrification?.drivingScore ?? null,
          homeCharging: driverElectrification?.homeCharging ?? null,
        };
      },
    );

    const rowsWithId = driversElectrification.map((av) => {
      const newDict: DriverElectrification & { id: string } = {
        ...av,
        id: av.uuid,
      };
      return newDict;
    });
    return sortByAlphabeticalOrder(rowsWithId, "asc", "name");
  }, [driversListInit, electroCompatibleDriversListInit]);

  useEffect(() => {
    if (driversListStatus === "success" && driversElectrificationList) {
      //TODO use later electroCompatibleDriversListStatus

      const driversSorted = sortByAlphabeticalOrder(
        driversElectrificationList,
        "asc",
        "name",
      );
      setListDisplayed(driversSorted);
    }
  }, [driversElectrificationList, driversListStatus]);

  const searchBarProps = {
    values: driversElectrificationList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("drivers.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof DriverElectrificationDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 220,
      },
      prioritizationScore: {
        type: "number",
        width: 200,
      },
      electroCompatibilityScore: {
        type: "number",
        width: 200,
      },
      drivingScore: {
        type: "number",
        width: 200,
      },
      homeCharging: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          return (
            value && (
              <StyledRow>
                <Badge
                  text={t(`electrification.homeCharging.${value}`)}
                  backgroundColor={
                    value === "COMPATIBLE"
                      ? colors[
                          "colors/system/informative/informative_veryLight"
                        ]
                      : value === "INCOMPATIBLE"
                        ? colors["colors/system/error/error_veryLight"]
                        : colors["colors/system/success/success_veryLight"]
                  }
                  textColor={colors["colors/text/black"]}
                />
              </StyledRow>
            )
          );
        },
      },
    };
  }, [t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "drivers") {
    return;
  }

  return (
    <MaterialUiTable<
      keyof DriverElectrificationDisplayed,
      DriverElectrificationDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;
